package com.sddamico.swigmore.api

import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.serialization.DeserializationStrategy
import kotlinx.serialization.SerializationStrategy
import org.w3c.dom.url.URLSearchParams
import org.w3c.fetch.RequestInit
import kotlin.js.json

class ApiException(val status: Int, val errorMessage: String, val field: String? = null) : Exception(errorMessage)

private suspend fun <T> apiRequest(
    method: String,
    url: String,
    body: String?,
    deserializer: DeserializationStrategy<T>,
): T {
    val init = js("{}").unsafeCast<RequestInit>()
    init.method = method
    init.headers = if (method == "POST") {
        json(
            "Accept" to "application/json",
            "Content-Type" to "application/json",
        )
    } else {
        json("Accept" to "application/json")
    }
    init.asDynamic().credentials = "same-origin"
    if (body != null) {
        init.body = body
    }

    val response = window.fetch(url, init).await()
    val text = response.text().await()

    if (!response.ok) {
        val errorMsg = try {
            apiJson.decodeFromString(ErrorResponse.serializer(), text)
        } catch (_: Exception) {
            null
        }
        throw ApiException(response.status.toInt(), errorMsg?.error ?: "Request failed with status ${response.status}", errorMsg?.field)
    }

    return apiJson.decodeFromString(deserializer, text)
}

/** `/api/[route]`, with the non-null [query] values as its query string. */
private fun apiUrl(route: String, query: Array<out Pair<String, Any?>>): String {
    val params = URLSearchParams()
    query.forEach { (name, value) -> if (value != null) params.append(name, value.toString()) }
    val search = params.toString()
    return if (search.isEmpty()) "/api/$route" else "/api/$route?$search"
}

internal suspend fun <T> apiGet(route: String, response: DeserializationStrategy<T>, vararg query: Pair<String, Any?>): T =
    apiRequest("GET", apiUrl(route, query), null, response)

internal suspend fun <Req, Res> apiPost(
    route: String,
    body: Req,
    request: SerializationStrategy<Req>,
    response: DeserializationStrategy<Res>,
): Res = apiRequest("POST", apiUrl(route, emptyArray()), apiJson.encodeToString(request, body), response)

internal suspend fun <Res> apiPost(route: String, response: DeserializationStrategy<Res>): Res =
    apiRequest("POST", apiUrl(route, emptyArray()), null, response)
